/**
 * The amount a partner types, as a string, from first keypress to the API
 * (MONEY-1, MONEY-2).
 *
 * There is no `parseFloat` anywhere in this file and that is the entire point.
 * The moment an amount becomes a JS number it is a float, and ৳1,20,000.10 is
 * not exactly representable as one. The server takes money as a string, the
 * database stores DECIMAL(14,2), and this keeps the client honest across the
 * only stretch where the discipline could quietly break: the keypad.
 *
 * So the "number" being edited is a string of digits with at most one decimal
 * point, and every operation is a string operation.
 */

const MAX_PAISA_DIGITS = 2;
/** ৳99,99,99,999.99 — comfortably past anything this farm will record, and
 *  well inside DECIMAL(14,2). A cap stops a stuck key producing nonsense. */
const MAX_TAKA_DIGITS = 9;

/** The digits and the point a keypad may send. */
export type AmountKey = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '.';

export const AMOUNT_KEYS: readonly AmountKey[] = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0'];

const split = (draft: string): [string, string | undefined] => {
  const [taka = '', paisa] = draft.split('.');
  return [taka, paisa];
};

/**
 * Appends one keypress, refusing anything that would make the draft invalid.
 *
 * Rejecting at the keypress rather than at submit means a partner can never
 * type an amount the server would refuse — the error they never see is worth
 * more than a good message about it.
 */
export function appendAmountKey(draft: string, key: AmountKey): string {
  const [taka, paisa] = split(draft);

  if (key === '.') {
    // One point only, and never as the first character: ".50" is ambiguous to
    // read on a small screen, "0.50" is not.
    if (paisa !== undefined) return draft;
    return draft === '' ? '0.' : `${draft}.`;
  }

  if (paisa !== undefined) {
    return paisa.length >= MAX_PAISA_DIGITS ? draft : `${draft}${key}`;
  }

  // No leading zeros: typing 0 then 5 means 5, not 05.
  if (taka === '0') return key === '0' ? draft : key;
  if (taka.length >= MAX_TAKA_DIGITS) return draft;

  return `${draft}${key}`;
}

/** Backspace. Removes one character, including a trailing point. */
export const deleteAmountKey = (draft: string): string => draft.slice(0, -1);

/**
 * True when the draft is something the server would accept as money.
 *
 * Deliberately stricter than "parses as a number": zero is not a real expense,
 * and a lone "0." is a half-typed amount rather than a value.
 */
export function isCompleteAmount(draft: string): boolean {
  if (!/^\d+(\.\d{1,2})?$/.test(draft)) return false;
  return /[1-9]/.test(draft);
}

/**
 * The canonical `123.45` form the API expects (MONEY-2).
 *
 * Pads rather than rounds: everything here is already at most two decimals, so
 * there is nothing to round and no rounding decision to get wrong.
 */
export function toApiAmount(draft: string): string {
  const [taka, paisa] = split(draft);
  return `${taka === '' ? '0' : taka}.${(paisa ?? '').padEnd(MAX_PAISA_DIGITS, '0')}`;
}

/**
 * The draft as it should appear on the keypad display.
 *
 * Grouped in the lakh style a partner reads, with the paisa left exactly as
 * typed — reformatting "12." into "12.00" while someone is mid-amount moves
 * the cursor out from under their thumb.
 */
export function formatAmountDraft(draft: string): string {
  if (draft === '') return '0';

  const [taka, paisa] = split(draft);
  const grouped = groupLakh(taka === '' ? '0' : taka);

  return paisa === undefined ? grouped : `${grouped}.${paisa}`;
}

/** Last three digits, then groups of two: 1,20,500 rather than 120,500 (P0-TR5). */
function groupLakh(digits: string): string {
  if (digits.length <= 3) return digits;

  const last3 = digits.slice(-3);
  const rest = digits.slice(0, -3);
  return `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${last3}`;
}
